'use client';

import * as React from 'react';
import { KEYFRAME_NAMES } from '@federa/const/design-token-keyframes';
import { animations } from '@federa/const/timing-tokens';
import { compactJoin } from '@federa/utils/strings/address-formatters';
import { colors } from '../css-refs.colors.ts';
import { borderRadius } from '../css-refs.effects.ts';
import { spacing } from '../css-refs.spacing.ts';

/**
 * THE skeleton surface -- the one decision "what colour is content that does not exist yet". Exported because a
 * skeleton CONTAINER (a card whose children are themselves skeleton parts) cannot be a `SkeletonBlock`, so without an
 * export its only recourse was to re-spell `colors.background.tertiary` privately, which several route `loading.tsx`
 * files did. `colors.background.tertiary` is a token many surfaces legitimately read for other reasons; what is homed
 * here is its use AS the skeleton surface, so a change to that choice lands in one place.
 */
export const SKELETON_SURFACE_STYLE: React.CSSProperties = {
	backgroundColor: colors.background.tertiary,
};

/**
 * THE PULSE IS THE DESIGN SYSTEM'S OWN KEYFRAME, INLINE (iss-8eb9d957, 2026-09-15). Every box here used to wear Tailwind's
 * `animate-pulse`, which is emitted only into bundles whose content scan sees the class: the host app animated, and
 * the mission board -- an inline-token surface with no utility layer -- held every skeleton's height and never
 * breathed. `KEYFRAME_NAMES.PULSE_DOT` is the same two-stop opacity breathe, shipped in base.css, which every surface
 * loads; the status dot took this cure first (`components/common/StatusDot.tsx`).
 */
const SKELETON_PULSE_STYLE: React.CSSProperties = {
	animation: `${KEYFRAME_NAMES.PULSE_DOT} ${animations.duration.crawl} infinite`,
};

// SkeletonBase (shared rendering core)

interface SkeletonBaseProps {
	/** Additional utility classes ahead of the caller's. */
	extraClass?: string;
	/** Shape-specific styles (dimensions, border-radius). */
	shapeStyle: React.CSSProperties;
	/** Caller-provided style overrides merged last. */
	style?: React.CSSProperties;
	/** Additional utility classes after the shape's own. */
	className?: string;
}

/**
 * EVERY SKELETON BOX IS HIDDEN FROM ASSISTIVE TECHNOLOGY, and that is the whole aria treatment these primitives get.
 *
 * A skeleton is a picture of content that does not exist yet: an empty `<div>` with a pulse animation. Left in the
 * accessibility tree it contributes nothing to read and, worse, a screen-reader user tabbing a loading list hears a run
 * of empty group nodes with no indication anything is coming. Of the two conventional treatments -- `role="status"` on
 * a wrapper with the boxes hidden, or `aria-busy` on the region being replaced -- these primitives can only own the
 * first half honestly: they render ONE box each and have no wrapper, so hiding the box is theirs to do and announcing
 * "loading" belongs to whichever surface owns the region (which is where a name for it exists).
 */
function SkeletonBase({ extraClass, shapeStyle, style, className }: SkeletonBaseProps) {
	const cls = compactJoin([extraClass, className], ' ');
	return <div className={cls} style={{ ...SKELETON_SURFACE_STYLE, ...SKELETON_PULSE_STYLE, ...shapeStyle, ...style }} aria-hidden="true" />;
}

// SkeletonOverlay (absolutely-positioned animated pulse layer)

interface SkeletonOverlayProps {
	/** Background style for the overlay layer (color/gradient). Defaults to the skeleton surface backgroundColor. */
	style?: React.CSSProperties;
	/** Additional utility classes appended after `absolute inset-0`. */
	className?: string;
}

/** Absolute-positioned pulse overlay for a relatively-positioned image frame; consumers control the background via `style`. */
export function SkeletonOverlay({ style, className }: SkeletonOverlayProps) {
	const cls = compactJoin(['absolute inset-0', className], ' ');
	// Hidden for the reason `SkeletonBase` states: a pulse layer over an image frame is decoration, not content.
	return <div className={cls} style={{ ...SKELETON_SURFACE_STYLE, ...SKELETON_PULSE_STYLE, ...style }} aria-hidden="true" />;
}

/** The two pass-through knobs every exported skeleton shape exposes. */
type SkeletonShapeProps = {
	/** Additional CSS properties merged last (caller wins). */
	style?: React.CSSProperties;
	/** Additional utility classes. */
	className?: string;
};

type SkeletonLineProps = SkeletonShapeProps & {
	/** CSS width (e.g. "70%", spacing token). Defaults to "100%". */
	width?: string;
	/** CSS height (e.g. spacing[4]). Defaults to spacing[4] (~16px). */
	height?: string;
	/** Border radius token. Defaults to borderRadius.lg. */
	radius?: string;
};

export function SkeletonLine({ width = '100%', height = spacing[4], radius = borderRadius.lg, style, className }: SkeletonLineProps) {
	return <SkeletonBase shapeStyle={{ width, height, borderRadius: radius }} style={style} className={className} />;
}

/**
 * A bare placeholder BOX -- no intrinsic dimensions, geometry supplied entirely by the caller's classes and style.
 * The shape every route-level `loading.tsx` needs and the one this module did not export: `SkeletonLine` bakes in a
 * width, a height and a radius, so a caller wanting an aspect-ratio tile or a class-driven card had to hand-roll the
 * div -- and six of them did, each re-spelling `backgroundColor: colors.background.tertiary` under a private name
 * (`CARD_STYLE`, `ROW_STYLE`, `BLOCK_STYLE`) beside the surface style this file already held privately.
 *
 * PROMOTING IT ALSO CLOSES AN ARIA HOLE, which is the half that is not a dedup. Those hand-rolled divs carried no
 * `aria-hidden`, so a screen-reader user on a loading route heard a run of empty group nodes -- exactly the failure
 * `SkeletonBase`'s docstring says these primitives exist to prevent. A skeleton box that is not reachable through
 * this module cannot honour that invariant, so the invariant needs the export rather than a comment.
 */
export function SkeletonBlock({ style, className }: SkeletonShapeProps) {
	return <SkeletonBase shapeStyle={{}} style={style} className={className} />;
}

/** `count` identical `SkeletonBlock`s -- the repeat idiom every grid and list skeleton spells, homed once. */
export function SkeletonBlockRepeat({ count, style, className }: SkeletonShapeProps & { count: number }) {
	return (
		<>
			{Array.from({ length: count }, (_, index) => (
				<SkeletonBlock key={index} style={style} className={className} />
			))}
		</>
	);
}

type SkeletonCircleProps = SkeletonShapeProps & {
	/** Diameter of the circle. Defaults to spacing[7] (~28px). */
	size?: string;
};

export function SkeletonCircle({ size = spacing[7], style, className }: SkeletonCircleProps) {
	return (
		<SkeletonBase
			extraClass="shrink-0"
			shapeStyle={{
				width: size,
				height: size,
				borderRadius: borderRadius.full,
			}}
			style={style}
			className={className}
		/>
	);
}
